import IconBin from "@shared/components/icons/Bin"
import IconBookLibrary from "@shared/components/icons/BookLibrary"
import IconCog1 from "@shared/components/icons/Cog1"
import IconCommonFileDouble from "@shared/components/icons/CommonFileDouble"
import IconCopy from "@shared/components/icons/Copy"
import IconCopyPaste from "@shared/components/icons/CopyPaste"
import IconDownload from "@shared/components/icons/Download"
import IconFileRotate from "@shared/components/icons/FileRotate"
import IconFlashColor from "@shared/components/icons/FlashColor"
import IconHistoryClock from "@shared/components/icons/HistoryClock"
import IconPalette from "@shared/components/icons/Palette"
import IconPrint from "@shared/components/icons/Print"
import IconPrintText from "@shared/components/icons/PrintText"
import IconSubtractCircle from "@shared/components/icons/SubtractCircle"
import IconSyncArrowClock from "@shared/components/icons/SyncArrowClock"
import {
  DrilldownMenu,
  DrilldownRow,
  HeaderWithBackArrow,
  MenuHeader,
  MenuRoot,
  MenuTitle,
  Row,
  RowContent,
  RowIcon,
  RowLabel,
  Rows,
  Section,
  SectionChevron,
  SectionHeading,
  SectionIcon,
  Sections,
  SectionTitle,
} from "@shared/components/menus/DrilldownMenu"
import { TemplateNameView } from "@shared/components/menus/views/TemplateNameView"
import type { UnifiedCardStack } from "@shared/models/card-stack"
import type { CourseColor } from "@shared/models/colors"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import { COURSE_COLOR_MAP } from "@shared/util/colors"
import { tracked } from "@supergrain/kernel/react"
import { ReplaceWithTemplateView } from "../ReplaceWithTemplateView"
import CourseHeaderMenuTrigger from "../unified-lesson-editable/CourseHeaderMenuTrigger"
import { expandedSections } from "./unit-menu-sections"
import { ChangeUnitColorView } from "./unit-menu-views/ChangeUnitColorView"
import type { UnitDeletionScope } from "./unit-menu-views/DeleteUnitView"
import { DeleteUnitView } from "./unit-menu-views/DeleteUnitView"

interface Props {
  unit: UnifiedCardStack & { attributes: { usedAs: "unit" } }
  course: Course
  planbook: Planbook
  /** Copies the unit to the user's clipboard (or prompts sign-in) and toasts. */
  copyUnit: () => void
  pasteUnit: () => void
  canPasteUnit: boolean
  /** Erases the unit, and — when the scope says so — the lessons it covers. */
  eraseUnit: (scope: UnitDeletionScope) => void
  printOrDownload: () => void
  browseUnitHistory: () => void
  /** Opens the template-library modal (the Replace with Template drilldown's gallery row). */
  openTemplateGallery: () => void
  /** Pastes the given template over this unit. */
  pasteTemplate: (templateId: string) => void
  saveAsTemplate: (name: string) => void
  /** Recolors the unit; null clears its color. */
  changeUnitColor: (color: CourseColor | null) => void
  /** Shows the readonly menu, regardless of permissions. */
  readonly?: boolean
}

export default tracked(function UnitMenu({
  unit,
  course,
  planbook,
  copyUnit,
  pasteUnit,
  canPasteUnit,
  eraseUnit,
  printOrDownload,
  browseUnitHistory,
  openTemplateGallery,
  pasteTemplate,
  saveAsTemplate,
  changeUnitColor,
  readonly = false,
}: Props) {
  const canEdit = (unit.meta?.permissions?.includes("can-edit-plan") ?? false) && !readonly
  const expandedIds = expandedSections.expandedIds

  const unitHeader = (title: string) =>
    function Header({ back }: { back: () => void }) {
      return (
        <HeaderWithBackArrow backTo="Unit Menu" onBack={back}>
          {title}
        </HeaderWithBackArrow>
      )
    }

  return (
    <DrilldownMenu
      ariaLabel="Unit menu"
      trigger={<CourseHeaderMenuTrigger label="Unit Menu" dataTest="unit menu trigger" />}
      header={
        <MenuHeader>
          <MenuTitle>Unit Menu</MenuTitle>
        </MenuHeader>
      }
    >
      {({ close }) => {
        const closeThen = (action: () => void) => () => {
          close()
          action()
        }
        return (
          <MenuRoot>
            <Sections>
              {canEdit ? (
                <>
                  <Section
                    typeaheadText="Copy and Paste"
                    isExpanded={expandedIds.has("copy-paste")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("copy-paste", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconCommonFileDouble} />
                        <SectionTitle>Copy and Paste</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <Row typeaheadText="Copy Unit" onAction={closeThen(copyUnit)}>
                        <RowContent>
                          <RowIcon icon={IconCopy} />
                          <RowLabel>Copy Unit</RowLabel>
                        </RowContent>
                      </Row>
                      {canPasteUnit ? (
                        <Row typeaheadText="Paste Unit" onAction={closeThen(pasteUnit)}>
                          <RowContent>
                            <RowIcon icon={IconCopyPaste} />
                            <RowLabel>Paste Unit</RowLabel>
                          </RowContent>
                        </Row>
                      ) : null}
                    </Rows>
                  </Section>

                  <Section
                    typeaheadText="Remove"
                    isExpanded={expandedIds.has("remove")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("remove", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconSubtractCircle} />
                        <SectionTitle>Remove</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <DrilldownRow
                        typeaheadText="Erase Unit"
                        header={unitHeader("Delete this unit?")}
                        body={({ back }) => (
                          <DeleteUnitView
                            onCancel={back}
                            onDelete={(scope) => {
                              close()
                              eraseUnit(scope)
                            }}
                          />
                        )}
                      >
                        <RowContent>
                          <RowIcon icon={IconBin} />
                          <RowLabel>Erase Unit</RowLabel>
                        </RowContent>
                      </DrilldownRow>
                    </Rows>
                  </Section>

                  <Section
                    typeaheadText="Print & Share"
                    isExpanded={expandedIds.has("print-share")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("print-share", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconPrintText} />
                        <SectionTitle>Print &amp; Share</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <Row typeaheadText="Download Unit" onAction={closeThen(printOrDownload)}>
                        <RowContent>
                          <RowIcon icon={IconDownload} />
                          <RowLabel>Download Unit</RowLabel>
                        </RowContent>
                      </Row>
                      <Row typeaheadText="Print Unit" onAction={closeThen(printOrDownload)}>
                        <RowContent>
                          <RowIcon icon={IconPrint} />
                          <RowLabel>Print Unit</RowLabel>
                        </RowContent>
                      </Row>
                    </Rows>
                  </Section>

                  <Section
                    typeaheadText="Templates"
                    isExpanded={expandedIds.has("templates")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("templates", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconFlashColor} className="size-5" />
                        <SectionTitle>Templates</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <DrilldownRow
                        typeaheadText="Replace Unit with Template"
                        header={unitHeader("Replace Unit")}
                        body={() => (
                          <ReplaceWithTemplateView
                            planbook={planbook}
                            onChoose={(templateId) => {
                              close()
                              pasteTemplate(templateId)
                            }}
                            onChooseFromGallery={closeThen(openTemplateGallery)}
                          />
                        )}
                      >
                        <RowContent>
                          <RowIcon icon={IconFileRotate} />
                          <RowLabel>Replace Unit with Template</RowLabel>
                        </RowContent>
                      </DrilldownRow>
                      <DrilldownRow
                        typeaheadText="Save Unit to Template Library"
                        header={unitHeader("What would you like to call this template?")}
                        body={({ back }) => (
                          <TemplateNameView
                            onCancel={back}
                            onSave={(name) => {
                              close()
                              saveAsTemplate(name)
                            }}
                          />
                        )}
                      >
                        <RowContent>
                          <RowIcon icon={IconBookLibrary} />
                          <RowLabel>Save Unit to Template Library</RowLabel>
                        </RowContent>
                      </DrilldownRow>
                    </Rows>
                  </Section>

                  <Section
                    typeaheadText="History"
                    isExpanded={expandedIds.has("history")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("history", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconHistoryClock} />
                        <SectionTitle>History</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <Row
                        typeaheadText="Browse Unit History"
                        onAction={closeThen(browseUnitHistory)}
                      >
                        <RowContent>
                          <RowIcon icon={IconSyncArrowClock} />
                          <RowLabel>Browse Unit History</RowLabel>
                        </RowContent>
                      </Row>
                    </Rows>
                  </Section>

                  <Section
                    typeaheadText="Settings"
                    isExpanded={expandedIds.has("settings")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("settings", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconCog1} />
                        <SectionTitle>Settings</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <DrilldownRow
                        typeaheadText="Change Unit Color"
                        header={unitHeader("Unit Color")}
                        body={() => <ChangeUnitColorView unit={unit} onSelect={changeUnitColor} />}
                      >
                        <RowContent>
                          <RowIcon
                            icon={({ className }) => (
                              <UnitColorIcon unit={unit} className={className} />
                            )}
                          />
                          <RowLabel>Change Unit Color</RowLabel>
                        </RowContent>
                      </DrilldownRow>
                    </Rows>
                  </Section>
                </>
              ) : (
                <>
                  <Section
                    typeaheadText="Copy"
                    isExpanded={expandedIds.has("copy-readonly")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("copy-readonly", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconCommonFileDouble} />
                        <SectionTitle>Copy</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <Row typeaheadText="Copy Unit" onAction={closeThen(copyUnit)}>
                        <RowContent>
                          <RowIcon icon={IconCopy} />
                          <RowLabel>Copy Unit</RowLabel>
                        </RowContent>
                      </Row>
                    </Rows>
                  </Section>
                  <Section
                    typeaheadText="Print"
                    isExpanded={expandedIds.has("print-readonly")}
                    onExpandedChange={(isExpanded) =>
                      expandedSections.setExpanded("print-readonly", isExpanded)
                    }
                    heading={
                      <SectionHeading>
                        <SectionIcon icon={IconPrintText} />
                        <SectionTitle>Print</SectionTitle>
                        <SectionChevron />
                      </SectionHeading>
                    }
                  >
                    <Rows>
                      <Row typeaheadText="Download Unit" onAction={closeThen(printOrDownload)}>
                        <RowContent>
                          <RowIcon icon={IconDownload} />
                          <RowLabel>Download Unit</RowLabel>
                        </RowContent>
                      </Row>
                      <Row typeaheadText="Print Unit" onAction={closeThen(printOrDownload)}>
                        <RowContent>
                          <RowIcon icon={IconPrint} />
                          <RowLabel>Print Unit</RowLabel>
                        </RowContent>
                      </Row>
                    </Rows>
                  </Section>
                </>
              )}
            </Sections>
          </MenuRoot>
        )
      }}
    </DrilldownMenu>
  )
})

// Keep the color read tracked so picks made in the open drilldown repaint the root row icon.
const UnitColorIcon = tracked(function UnitColorIcon({
  unit,
  className,
}: {
  unit: UnifiedCardStack & { attributes: { usedAs: "unit" } }
  className?: string
}) {
  const color = unit.attributes.color
  return <IconPalette activeColor={color ? COURSE_COLOR_MAP[color] : null} className={className} />
})
